import React, { useId, useState } from "react"
import * as styles from "./ExpandingText.module.scss"

type ExpandingTextProps = {
  /** Text rendered as "expanding text". HTML is supported */
  text: string
  /** CSS length value for text container 'max-height'. Defaults to `8rem` */
  closedMaxHeight?: string
  /** Label for button hiding expanded text. Defaults to "Show less" */
  lessLabel?: string
  /** Label for button showing expanded text. Defaults to "Show more" */
  moreLabel?: string
  /** Parent background color as valid CSS color. Needed for text fade out effect. Defaults to `transparent`. */
  backgroundColor: string
}

/**
 * Custom extension to `React.CSSProperties` to allow specific CSS properties
 */
interface CustomCSSProps extends React.CSSProperties {
  "--background-color": string
  "--closed-max-height": string
}

const ExpandingText = (props: ExpandingTextProps) => {
  // console.debug("Rendering ExpandingText", props)

  const textId = useId() // generate unique ID for accessibility attributes
  const [isOpen, setIsOpen] = useState(false)

  if (!props.text) return null

  const containerStyles: CustomCSSProps = {
    // pass backgroundColor as CSS custom property so that we can set a pseudo element color using var()
    "--background-color": props.backgroundColor || "transparent", // NOTE "inherit" won't work here. This is used on a pseudo element background-image gradient
    "--closed-max-height": props.closedMaxHeight || "8rem",
  }

  return (
    <>
      <div
        id={textId}
        role="region"
        className={`${styles.text} ${isOpen ? styles.open : styles.closed}`}
        style={containerStyles}
        dangerouslySetInnerHTML={{ __html: props.text }}
      />
      <button
        className={styles.moreButton}
        onClick={() => setIsOpen(!isOpen)}
        aria-controls={textId}
        aria-expanded={isOpen}>
        {isOpen ? props.lessLabel || "Show less" : props.moreLabel || "Show more"}
      </button>
    </>
  )
}

export default ExpandingText
